<script lang="ts">
  import Chart from '../charts/Chart.svelte';
  import Dots from '../charts/Dots.svelte';
  import Readout, { type Item } from '../charts/Readout.svelte';
  import { decimals, niceTicks, polyline, segments } from '../../utils/chart';
  import { locale, t } from '../../i18n/index.ts';

  // PointChart is a point per hour, day, week, or month on a Health card
  // (spec 007 FR-019, 007 FR-020) in the shared chart language (spec 010
  // 010 FR-033): heart rate, a body measurement, or a vital. A line series
  // joins neighboring points and breaks at a missing value (007 FR-022); a
  // faint one is a measure taken once a day, drawn as pale points under its
  // average; a smooth one, such as a 7-day average or weight's trend, runs
  // through gaps without points (spec 007 FR-103). band shades each
  // slot's lowest to highest, as a day's heart rate does, or a normal range.
  // Hovering or tapping reads a slot above the chart, and the sr-only table
  // gives a screen reader every value.
  interface Series {
    name: string;
    color: string;
    values: (number | null | undefined)[];
    kind?: 'line' | 'faint' | 'smooth';
  }
  interface Props {
    series: Series[];
    band?: ({ lo: number; hi: number } | null)[];
    bandName?: string;
    labels?: string[];
    ticks?: { at: number; text: string }[];
    names: string[];
    column: string;
    caption: string;
    format: (v: number) => string;
    // unit heads the value scale.
    unit?: string;
    // short draws a smaller chart, as each body measurement and vital has.
    short?: boolean;
  }
  let { series, band, bandName, labels, ticks, names, column, caption, format, unit, short = false }: Props = $props();

  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  let shown = $derived(hover ?? at);

  let n = $derived(Math.max(1, ...series.map((s) => s.values.length), band?.length ?? 0));
  let positions = $derived(Array.from({ length: n }, (_, i) => (i + 0.5) / n));
  let all = $derived([
    ...series.flatMap((s) => s.values.filter((v): v is number => v != null)),
    ...(band ?? []).flatMap((b) => (b ? [b.lo, b.hi] : [])),
  ]);
  // scale spans at least a twentieth of the values' size, so a change of a
  // percent does not fill the chart as if it were large.
  let scale = $derived.by(() => {
    if (!all.length) return niceTicks(0, 1);
    const lo = Math.min(...all), hi = Math.max(...all);
    const pad = Math.max(0, Math.abs(hi) * 0.05 - (hi - lo)) / 2;
    return niceTicks(lo - pad, hi + pad);
  });
  let xTicks = $derived(labels ? labels.map((l, i) => ({ at: positions[i], text: l })) : (ticks ?? []));

  const lines = (s: Series) => segments(s.values.map((v, i) => ({ at: positions[i], v })), s.kind === 'smooth').filter((sg) => sg.length > 1).map((sg) => polyline(sg, scale.lo, scale.hi));

  // bandPolys are the band's unbroken stretches as polygons; a lone slot is
  // widened to a third of its width each side so it still shows.
  let bandPolys = $derived.by(() => {
    if (!band) return [];
    const polys: string[] = [];
    let cur: [number, { lo: number; hi: number }][] = [];
    const flush = () => {
      if (!cur.length) return;
      const w = 1 / n / 3;
      const pts: [number, number][] = cur.length === 1
        ? [[positions[cur[0][0]] - w, cur[0][1].hi], [positions[cur[0][0]] + w, cur[0][1].hi], [positions[cur[0][0]] + w, cur[0][1].lo], [positions[cur[0][0]] - w, cur[0][1].lo]]
        : [...cur.map(([i, b]) => [positions[i], b.hi] as [number, number]), ...cur.slice().reverse().map(([i, b]) => [positions[i], b.lo] as [number, number])];
      polys.push(polyline(pts, scale.lo, scale.hi));
      cur = [];
    };
    band.forEach((b, i) => {
      if (b) cur.push([i, b]);
      else flush();
    });
    flush();
    return polys;
  });

  let items = $derived.by((): Item[] => {
    const out: Item[] = series.map((s) => {
      const v = shown != null ? s.values[shown] : null;
      return { label: s.name, value: shown == null ? undefined : v != null ? format(v) : t('healthNoData'), color: s.color, mark: s.kind === 'faint' ? 'faint' : 'line' };
    });
    if (band && bandName) {
      const b = shown != null ? band[shown] : null;
      out.push({ label: bandName, value: shown == null ? undefined : b ? t('healthRange', { low: format(b.lo), high: format(b.hi) }) : t('healthNoData'), color: series[0]?.color ?? 'var(--theme-text)', mark: 'band' });
    }
    return out;
  });
  let valueText = $derived.by(() => {
    if (shown == null) return t('healthNothingPicked');
    const each = series.map((s) => {
      const v = s.values[shown!];
      return v != null ? t('healthSeriesValue', { series: s.name, value: format(v) }) : t('healthSeriesNoData', { series: s.name });
    });
    return t('healthChartValue', { name: names[shown], value: new Intl.ListFormat(locale(), { type: 'unit', style: 'long' }).format(each) });
  });
</script>

<div class="space-y-2">
  {#if items.length === 1}
    <Readout title={shown != null ? t('healthChartValue', { name: names[shown], value: items[0].value }) : t('healthHoverPoint')} idle={shown == null} items={[]} />
  {:else}
    <Readout title={shown != null ? names[shown] : t('healthHoverPoint')} idle={shown == null} {items} />
  {/if}
  <Chart lo={scale.lo} hi={scale.hi} {unit} yTicks={scale.ticks.map((v) => ({ v, text: v.toFixed(decimals(scale.step)) }))}
    {xTicks} xGrid={!labels} height={short ? 'h-24' : 'h-32'} {positions} bind:at bind:hover label={t('healthChartLabel', { caption })} {valueText}
    dots={shown != null ? series.filter((s) => s.kind !== 'faint' && s.values[shown!] != null).map((s) => ({ v: s.values[shown!]!, color: s.color })) : []}>
    {#snippet plot()}
      {#each bandPolys as pts, k (k)}
        <polygon points={pts} fill={series[0]?.color} fill-opacity="0.2" />
      {/each}
      {#each series.filter((s) => s.kind !== 'faint') as s (s.name)}
        {#each lines(s) as l, k (k)}
          <polyline points={l} fill="none" stroke={s.color} stroke-width="2" stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke" />
        {/each}
      {/each}
    {/snippet}
    {#snippet overlay()}
      {#each series as s (s.name)}
        {#if s.kind === 'faint' || ((s.kind ?? 'line') === 'line' && n <= 31)}
          <Dots dots={s.values.map((v, i) => ({ at: positions[i], v }))} color={s.color} lo={scale.lo} hi={scale.hi} faint={s.kind === 'faint'} />
        {/if}
      {/each}
    {/snippet}
  </Chart>
</div>
<table class="sr-only">
  <caption>{caption}</caption>
  <thead>
    <tr><th>{column}</th>{#each series as s (s.name)}<th>{s.name}</th>{/each}{#if band}<th>{bandName}</th>{/if}</tr>
  </thead>
  <tbody>
    {#each names as name, i (i)}
      {#if series.some((s) => s.values[i] != null) || band?.[i]}
        <tr>
          <td>{name}</td>
          {#each series as s (s.name)}<td>{s.values[i] != null ? format(s.values[i]!) : t('healthNoData')}</td>{/each}
          {#if band}<td>{band[i] ? t('healthRangeWords', { low: format(band[i]!.lo), high: format(band[i]!.hi) }) : t('healthNoData')}</td>{/if}
        </tr>
      {/if}
    {/each}
  </tbody>
</table>
